import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Command } from "cmdk";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Workflow,
  Bot,
  UserCircle2,
  Boxes,
  RotateCcw,
  Moon,
  Sun,
  CheckCheck,
  MessagesSquare,
} from "lucide-react";
import { pages, groups, type PageId } from "../pages/registry";

const groupLabel: Record<string, string> = Object.fromEntries(groups.map((g) => [g.id, g.label]));
import { BridgeApi, BusApi, SkillsApi } from "../lib/api";
import { qk } from "../lib/query";
import { useTheme } from "../lib/theme";
import { useToast } from "../lib/toast";
import { cn } from "../lib/cn";

export function CommandPalette({
  open,
  onOpenChange,
  onGoto,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGoto: (id: PageId) => void;
}) {
  const [query, setQuery] = useState("");
  const { theme, toggle } = useTheme();
  const toast = useToast();
  const qc = useQueryClient();

  // Defer fetching these until the palette opens
  const { data: cfg } = useQuery({
    queryKey: qk.bridge.config,
    queryFn: BridgeApi.config,
    enabled: open,
  });
  const { data: skills } = useQuery({
    queryKey: qk.skills.list,
    queryFn: SkillsApi.list,
    enabled: open,
  });

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const restart = useMutation({
    mutationFn: BridgeApi.restart,
    onSuccess: () => {
      toast.success("agent-bridge restart issued");
      qc.invalidateQueries({ queryKey: qk.bridge.status });
      onOpenChange(false);
    },
    onError: (err) => toast.error("Restart failed", String(err)),
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const msgs = await BusApi.messages({ unread_only: true, limit: 500 });
      for (const m of msgs) await BusApi.markRead(m.id, true);
    },
    onSuccess: () => {
      toast.success("All bus messages marked read");
      qc.invalidateQueries({ queryKey: ["bus"] });
      onOpenChange(false);
    },
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] data-[state=open]:animate-fade-in" />
        <Dialog.Content
          className={cn(
            "fixed top-[15vh] left-1/2 -translate-x-1/2 z-[300] w-full max-w-xl",
            "panel-elevated shadow-xl outline-none animate-fade-in",
          )}
        >
          <Dialog.Title className="sr-only">Command Palette</Dialog.Title>
          <Command className="w-full" loop>
            <div className="flex items-center gap-2 px-3 h-11 border-b border-border">
              <Search className="size-3.5 text-fg-subtle" />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                autoFocus
                placeholder="Jump to page, route, agent, or action…"
                className="flex-1 bg-transparent text-sm text-fg placeholder:text-fg-subtle focus:outline-none border-none p-0"
              />
              <kbd className="kbd">esc</kbd>
            </div>
            <Command.List className="max-h-[60vh] overflow-y-auto p-1">
              <Command.Empty className="px-3 py-6 text-center text-sm text-fg-muted">
                No matches.
              </Command.Empty>

              <Section heading="Actions">
                <Item
                  value="restart bridge"
                  onSelect={() => restart.mutate()}
                  icon={RotateCcw}
                  label="Restart agent-bridge"
                  kbd="R"
                />
                <Item
                  value="mark all read inbox"
                  onSelect={() => markAllRead.mutate()}
                  icon={CheckCheck}
                  label="Mark all bus messages read"
                />
                <Item
                  value="toggle theme"
                  onSelect={() => { toggle(); onOpenChange(false); }}
                  icon={theme === "dark" ? Sun : Moon}
                  label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
                />
              </Section>

              <Section heading="Pages">
                {pages.map((p) => {
                  const Icon = p.icon;
                  return (
                    <Item
                      key={p.id}
                      value={`page ${p.id} ${p.label} ${p.group}`}
                      onSelect={() => { onGoto(p.id); onOpenChange(false); }}
                      icon={Icon}
                      label={p.label}
                      meta={groupLabel[p.group] || undefined}
                    />
                  );
                })}
              </Section>

              {cfg && cfg.routes.length > 0 && (
                <Section heading="Routes">
                  {cfg.routes.map((r) => (
                    <Item
                      key={r.name}
                      value={`route ${r.name} ${r.persona} ${r.match.chat_jid ?? ""}`}
                      onSelect={() => {
                        onGoto("routes");
                        window.location.hash = `/routes?name=${encodeURIComponent(r.name)}`;
                        onOpenChange(false);
                      }}
                      icon={Workflow}
                      label={r.name}
                      meta={r.persona ?? r.sink_type ?? undefined}
                    />
                  ))}
                </Section>
              )}

              {cfg && Object.keys(cfg.agents).length > 0 && (
                <Section heading="Agents">
                  {Object.keys(cfg.agents).map((a) => (
                    <Item
                      key={a}
                      value={`agent ${a}`}
                      onSelect={() => { onGoto("agents"); onOpenChange(false); }}
                      icon={Bot}
                      label={a}
                    />
                  ))}
                </Section>
              )}

              {cfg && Object.keys(cfg.personas).length > 0 && (
                <Section heading="Personas">
                  {Object.keys(cfg.personas).map((p) => (
                    <Item
                      key={p}
                      value={`persona ${p}`}
                      onSelect={() => { onGoto("personas"); onOpenChange(false); }}
                      icon={UserCircle2}
                      label={p}
                    />
                  ))}
                </Section>
              )}

              {skills && skills.length > 0 && (
                <Section heading="Skills">
                  {skills.slice(0, 50).map((s) => (
                    <Item
                      key={s.name}
                      value={`skill ${s.name} ${s.description}`}
                      onSelect={() => { onGoto("catalog"); onOpenChange(false); }}
                      icon={Boxes}
                      label={s.name}
                      meta={s.description.slice(0, 60)}
                    />
                  ))}
                </Section>
              )}
            </Command.List>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <Command.Group
      heading={heading}
      className="text-[10px] uppercase tracking-wider text-fg-subtle px-1 py-1"
    >
      {children}
    </Command.Group>
  );
}

function Item({
  value,
  onSelect,
  icon: Icon,
  label,
  meta,
  kbd,
}: {
  value: string;
  onSelect: () => void;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  meta?: string;
  kbd?: string;
}) {
  // Suppress the unused warning for MessagesSquare which we may want later
  void MessagesSquare;
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className="flex items-center gap-2.5 px-2 py-1.5 rounded-md text-sm text-fg cursor-pointer data-[selected=true]:bg-surface-2"
    >
      <Icon className="size-3.5 text-fg-subtle flex-shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {meta && <span className="text-[10px] text-fg-subtle font-mono truncate max-w-[12rem]">{meta}</span>}
      {kbd && <kbd className="kbd ml-1">{kbd}</kbd>}
    </Command.Item>
  );
}
